import React, { useState } from 'react';
import { TextField } from '@ofself/shell';
import { PersonFace } from '../../components/nucleus/faces';
import Inspector, { Band, Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { useToast } from '../../components/common';
import { usePlace } from '../../nucleus/place';
import { FormBand } from './selfForm';
import { nameOf, IDENTITY_FIELDS } from './selfData';
import { plural } from '../../nucleus/format';

// A face: the photo where there is one (a failed signed URL falls back), the
// person glyph otherwise — never initials.
export function IdentityFace({ url, size = 50 }) {
    return <PersonFace url={url} size={size} />;
}

// Pick a photo from anywhere an identity's actions stand (the bar or a
// right-click), without a file input mounted in either.
function pickImage() {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = () => resolve(input.files?.[0] || null);
        input.click();
    });
}

// ONE IDENTITY'S ACTIONS — the bar and the right-click menu read this one array.
export function identityActions({ node, scope, usage, identities, open, onChanged, onDeleted, toast }) {
    const name = nameOf(node);
    const isDefault = node.id === scope.defaultId;
    const lock = scope.canEdit ? {} : { disabled: true, hint: scope.denied };
    const following = usage?.follows_default || 0;
    const rides = usage?.by_identity?.[String(node.id)] || { relationships: 0, realms: 0 };
    const fallback = nameOf((identities || []).find((n) => n.id === scope.defaultId) || {});
    const hasPhoto = !!node.value_json?.avatar_file_id;

    const lines = [];
    if (rides.relationships) {
        lines.push(`${plural(rides.relationships, 'relationship')} present${rides.relationships === 1 ? 's' : ''} it, and will fall back to ${isDefault ? 'no identity until you pick a new default' : `the default (${fallback})`}.`);
    }
    if (rides.realms) lines.push(`${plural(rides.realms, 'realm')} name${rides.realms === 1 ? 's' : ''} it.`);
    if (isDefault) lines.push('It is the default, so new connections have none until another is chosen.');
    lines.push('This can’t be undone.');

    return [
        open && { id: 'open', label: `About ${name}`, run: () => open(node.id), hidden: !open },
        !isDefault && {
            id: 'default', label: 'Make default', primary: true, ...lock,
            confirm: following > 0 ? {
                title: `Make ${name} the default?`,
                body: `${plural(following, 'relationship')} follow${following === 1 ? 's' : ''} the default and will present ${name}. Relationships that name their own identity are unaffected.`,
                label: 'Make default',
            } : undefined,
            run: async () => {
                await scope.setDefault(node.id);
                await scope.refresh?.();
                toast.success(`${name} is the default now`);
                await onChanged();
            },
        },
        {
            id: 'photo', label: hasPhoto ? 'Change photo' : 'Add photo', ...lock,
            run: async () => {
                const file = await pickImage();
                if (!file) return;
                const res = await scope.upload(file);
                const fileId = (res?.node || res)?.id;
                // Only the changed key: PUT merges.
                await scope.update(node.id, { value_json: { avatar_file_id: fileId } });
                toast.success('Photo saved');
                await onChanged();
            },
        },
        hasPhoto && {
            id: 'unphoto', label: 'Remove photo', danger: true, ...lock,
            confirm: { title: `Remove ${name}’s photo?`, body: 'The plain person mark shows in its place until you add another.', label: 'Remove' },
            run: async () => {
                await scope.clearPhoto(node.id);
                toast.success('Photo removed');
                await onChanged();
            },
        },
        {
            id: 'delete', label: 'Delete', danger: true, ...lock,
            confirm: { title: `Delete ${name}?`, body: lines.join(' ') },
            run: async () => {
                await scope.remove(node.id);
                if (isDefault) await scope.refresh?.();
                toast.success(`${name} is deleted`);
                await onDeleted();
            },
        },
    ].filter(Boolean);
}

export default function IdentityInspector({ node, photo, scope, usage, identities, onClose, onChanged, onDeleted }) {
    const toast = useToast();
    const { to, navigate } = usePlace();
    const isDefault = node.id === scope.defaultId;
    const rides = usage?.by_identity?.[String(node.id)] || { relationships: 0, realms: 0 };
    const actions = identityActions({ node, scope, usage, identities, onChanged, onDeleted, toast });

    return (
        <Inspector
            face={<IdentityFace name={nameOf(node)} url={photo} size={64} />}
            tag={`Identity${isDefault ? ' · Default' : ''}`}
            title={nameOf(node)}
            sub={isDefault ? 'Presented wherever nothing names another face.' : 'Presented where a realm or a relationship names it.'}
            actions={actions}
            onClose={onClose}
        >
            <Band title={scope.kind === 'group' ? scope.fallbackName || 'This group' : 'Self'}>
                <Facts items={[
                    { label: 'Presented', value: isDefault ? 'By default' : 'When named' },
                    { label: 'Relationships', value: String(rides.relationships || 0) },
                    { label: 'Realms naming it', value: String(rides.realms || 0) },
                    isDefault && { label: 'Follow the default', value: String(usage?.follows_default || 0) },
                ]} />
            </Band>
            <NameForm key={node.id} node={node} scope={scope} onChanged={onChanged} />
            <Band title="Where it is named" aside={<button type="button" className="nx-link" onClick={() => navigate(to('realms'))}>Realms</button>}>
                {(rides.named_by || []).map((r) => (
                    <Row key={r.id} name={r.name} sub="Realm · presents this identity" end="Open"
                        onPress={() => navigate(to('realms', null, `open=${encodeURIComponent(r.id)}`))} />
                ))}
                {!rides.named_by?.length && <Note>No realm names it.</Note>}
                <Note>A realm can name the face an app sees through it. Change that on the realm; this face follows.</Note>
            </Band>
        </Inspector>
    );
}

// Its name (written to both title and display_name) and, for a person, first
// and last name. Save at the band's end, like every Self form.
function NameForm({ node, scope, onChanged }) {
    const toast = useToast();
    const [name, setName] = useState(nameOf(node) === 'Untitled' ? '' : nameOf(node));
    const [fields, setFields] = useState(node.value_json || {});
    const shown = IDENTITY_FIELDS.filter((f) => scope.kind === 'personal' || !f.personalOnly);
    const trimmed = name.trim();
    const dirty = trimmed !== nameOf(node) || shown.some((f) => (fields[f.key] || '') !== (node.value_json?.[f.key] || ''));

    return (
        <FormBand
            title="Name"
            lead={scope.canEdit ? undefined : `${scope.denied}.`}
            acts={[{
                id: 'save', label: 'Save', primary: true,
                disabled: !scope.canEdit || !dirty || !trimmed,
                hint: scope.canEdit ? undefined : scope.denied,
                run: async () => {
                    const value_json = { ...fields, display_name: trimmed };
                    await scope.update(node.id, { title: trimmed, value_json });
                    toast.success('Saved');
                    await onChanged();
                },
            }]}
        >
            <TextField id="self-identity-name" label="Name" value={name} disabled={!scope.canEdit} onChange={(e) => setName(e.target.value)}
                placeholder={scope.kind === 'group' ? 'How this group is known' : 'How you want to be known'} />
            {shown.map((f) => (
                <TextField key={f.key} id={`self-identity-${f.key}`} label={f.label} value={fields[f.key] || ''} disabled={!scope.canEdit}
                    onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} />
            ))}
        </FormBand>
    );
}
